import { createFileRoute, Link } from "@tanstack/react-router";
import { Section, SectionHeader } from "@/components/Section";
import { CTASection } from "@/components/CTASection";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Reveal } from "@/components/Reveal";
import { SITE } from "@/lib/site";
import { seoTitle, seoDescription } from "@/lib/seo-meta";

const TITLE = seoTitle("Sobre a Dedetizadora em Chapecó — Controle de Pragas");
const DESC = seoDescription(
  "Conheça a Dedetizadora em Chapecó: empresa especializada em controle de pragas urbanas com equipe técnica, produtos registrados e atendimento em toda a região oeste de SC.",
);

export const Route = createFileRoute("/sobre")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: "/sobre" },
    ],
    links: [{ rel: "canonical", href: `${SITE.baseUrl}/sobre` }],
  }),
  component: Sobre,
});

const values = [
  { t: "Técnica antes de químico", d: "Primeiro vistoriamos, identificamos a praga e o foco. O produto é a última etapa — nunca a primeira." },
  { t: "Segurança para família e pets", d: "Aplicamos produtos registrados no Ministério da Saúde, com EPI completo e orientação clara sobre o tempo de retorno." },
  { t: "Garantia contratual", d: "3 a 6 meses de garantia. Se a praga voltar dentro do prazo, refazemos o serviço sem custo adicional." },
  { t: "Laudo aceito pela Vigilância", d: "Emitimos certificado técnico com produtos, lote e responsável — pronto para restaurantes, clínicas e indústrias." },
];

const method = [
  "Vistoria técnica gratuita no imóvel para identificar a praga e o foco",
  "Orçamento por escrito enviado por WhatsApp, sem compromisso",
  "Aplicação com técnico habilitado, EPI e produtos registrados",
  "Orientação sobre preparo do ambiente e tempo de retorno",
  "Certificado de execução com produtos, lote e responsável técnico",
  "Manutenção preventiva mensal, bimestral ou trimestral disponível",
];

function Sobre() {
  return (
    <>
      <section className="hero-bg">
        <div className="container-svg pt-12 pb-12 md:pt-20 md:pb-16">
          <Breadcrumbs items={[{ label: "Início", to: "/" }, { label: "Sobre" }]} />
          <div className="mt-6 max-w-3xl">
            <span className="eyebrow">Sobre a empresa</span>
            <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl">
              <span className="text-gradient">{SITE.name}</span> — controle de pragas técnico e seguro.
            </h1>
            <p className="mt-4 text-muted-foreground md:text-lg">
              Somos uma dedetizadora com base em Chapecó/SC especializada em pragas urbanas: baratas, ratos, cupins, formigas, aranhas e escorpiões. Atendemos casas, apartamentos, comércios, restaurantes, condomínios e indústrias em Chapecó e cidades da região oeste catarinense.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={SITE.whatsappUrl} target="_blank" rel="noopener" className="btn-accent">Falar no WhatsApp</a>
              <Link to="/contato" className="btn-ghost">Agendar vistoria</Link>
            </div>
          </div>
        </div>
      </section>

      <Section>
        <SectionHeader eyebrow="Nossos valores" title="Como trabalhamos" />
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {values.map((v, i) => (
            <Reveal key={v.t} delay={((i % 4) + 1) as 1 | 2 | 3 | 4}>
              <div className="card-glass">
                <h3 className="text-lg font-semibold">{v.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{v.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section className="bg-[color:var(--color-sidebar)]">
        <Reveal>
          <div className="grid gap-8 lg:grid-cols-2">
            <div>
              <span className="eyebrow">Metodologia</span>
              <h2 className="mt-3 text-2xl font-bold tracking-tight md:text-3xl">Como executamos cada serviço</h2>
              <p className="mt-4 text-muted-foreground">
                Todo trabalho segue um fluxo claro: vistoria técnica, identificação da praga, escolha do produto adequado, aplicação com EPI e entrega de certificado. O objetivo é resolver o problema e evitar a reincidência.
              </p>
            </div>
            <ul className="grid gap-3">
              {method.map((m) => (
                <li key={m} className="flex items-start gap-3 rounded border border-border bg-card p-4">
                  <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded bg-accent text-primary">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden>
                      <path d="m5 12 5 5L20 7" />
                    </svg>
                  </span>
                  <span className="text-sm">{m}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </Section>

      <Section>
        <Reveal>
          <div className="rounded border-2 border-primary bg-card p-8 md:p-10">
            <h2 className="text-2xl font-bold">Regularidade e responsabilidade técnica</h2>
            <p className="mt-4 text-sm text-muted-foreground">
              Atuamos com os produtos registrados no Ministério da Saúde e Anvisa e emitimos certificado de execução com todas as informações exigidas pela Vigilância Sanitária de Chapecó — essencial para bares, restaurantes, mercados, clínicas e escolas.
            </p>
            <p className="mt-3 text-sm text-muted-foreground">
              Dúvidas sobre um serviço específico? Escreva para <a href={`mailto:${SITE.email}`} className="text-primary underline font-semibold">{SITE.email}</a>.
            </p>
          </div>
        </Reveal>
      </Section>

      <CTASection title="Vamos resolver sua praga em Chapecó" text="Vistoria técnica gratuita e orçamento sem compromisso pelo WhatsApp." buttonLabel="Chamar no WhatsApp" />
    </>
  );
}
